import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Progress, message } from 'antd'
import { useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { addProgress, finish } from '../../redux/slices/office-check-downloads'
import useInterval from '../../hooks/useInterval'
import api from '../../api'
import { downloadZip } from '../../lib/download'
import sleep from '../../lib/sleep'

const REFETCHING_TIME = 5000

function xhrAsync(url, handleProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.responseType = 'blob'
    xhr.open('GET', url)

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response)
      } else {
        reject(new Error(`${xhr.statusText} - ${xhr.status}`))
      }
    }

    xhr.addEventListener('progress', handleProgress)

    xhr.onerror = () => reject(new Error(`${xhr.statusText} - ${xhr.status}`))
    xhr.send()
  })
}

const useStyles = createUseStyles(theme => ({
  label: {
    lineHeight: '0.75',
    color: 'white',
    fontSize: '13px',
  },
  container: {
    marginTop: '12px',
    '&:first-child': {
      marginTop: 0,
    },
  },
}))

const getZipFile = async (practiceId, fromDate, toDate) => {
  const { downloadUrl, filename, error } = await api.officeChecks.downloadFile(
    practiceId,
    fromDate,
    toDate,
  )

  if (error) {
    throw new Error('Error building zip file')
  }

  if (downloadUrl) return { downloadUrl, filename }

  await sleep(5000) // wait 5 seconds to make the request again

  return getZipFile(practiceId, fromDate, toDate)
}

const downloadStatusMap = {
  finished: 'success',
  loading: 'active',
  error: 'exception',
}

function OfficeCheckDownload({ practiceId, checkDate, progress }) {
  const dispatch = useDispatch()
  const [buildingZip, setBuildingZip] = useState('loading')
  const [downloadStatus, setDownloadStatus] = useState('idle')
  const classes = useStyles()

  // Split checkDate key back into from and to dates
  const [fromDate, toDate] =
    checkDate.split('-').length === 6
      ? [checkDate.slice(0, 10), checkDate.slice(11, 21)]
      : [checkDate, checkDate]

  const getFileProgressZip = async () => {
    try {
      const {
        totalFiles,
        addedFiles,
        finishedAt,
      } = await api.officeChecks.getBatch(practiceId, fromDate, toDate)

      const zipProgress = parseInt((addedFiles * 50) / totalFiles, 0)
      dispatch(addProgress(practiceId, fromDate, toDate, zipProgress))

      if (finishedAt && zipProgress >= 50) {
        setBuildingZip('finished')
      }
    } catch (err) {
      setBuildingZip('error')
      dispatch(finish(practiceId, fromDate, toDate))
      message.error(`Something went wrong: ${err.message}`)
    }
  }

  const processDownload = useCallback(async () => {
    const { downloadUrl, filename } = await getZipFile(
      practiceId,
      fromDate,
      toDate,
    )

    if (!downloadUrl) {
      throw new Error('Zip url is not present!')
    }

    const response = await xhrAsync(downloadUrl, event => {
      const downloadProgress = parseInt((event.loaded * 50) / event.total, 0)
      dispatch(addProgress(practiceId, fromDate, toDate, 50 + downloadProgress))
    })
    await downloadZip(response, filename)
  }, [dispatch, practiceId, fromDate, toDate])

  const downloadReportsZip = useCallback(async () => {
    try {
      setDownloadStatus('loading')
      await processDownload()
      setDownloadStatus('finished')

      setTimeout(() => {
        dispatch(finish(practiceId, fromDate, toDate))
      }, 3000)
    } catch (e) {
      message.error(`There was a problem downloading the reports: ${e.message}`)
      setDownloadStatus('error')
      dispatch(finish(practiceId, fromDate, toDate))
    }
  }, [dispatch, practiceId, fromDate, toDate, processDownload])

  useInterval(
    getFileProgressZip,
    buildingZip === 'loading' ? REFETCHING_TIME : null,
  )

  React.useEffect(() => {
    if (buildingZip === 'finished') {
      downloadReportsZip()
    }
  }, [downloadReportsZip, buildingZip])

  return (
    <div className={classes.container}>
      <div className={classes.label}>
        {buildingZip === 'loading' && <>Generating zip file</>}
        {downloadStatus === 'loading' && 'Downloading the zip file'}
        {downloadStatus === 'finished' && 'The download has been completed'}
        {downloadStatus === 'error' &&
          'There was an error downloading the zip file'}
      </div>
      <Progress percent={progress} status={downloadStatusMap[downloadStatus]} />
    </div>
  )
}

OfficeCheckDownload.propTypes = {
  practiceId: PropTypes.string,
  checkDate: PropTypes.string,
  progress: PropTypes.number,
}

OfficeCheckDownload.defaultProps = {
  practiceId: undefined,
  checkDate: undefined,
  progress: 0,
}

export default OfficeCheckDownload
